import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../../lib/auth';
import { useContractorAuthStore } from '../../lib/contractorAuth';
import { contractorApi } from '../../lib/contractorApi';
import { API_BASE } from '../../lib/apiBase';

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 16px',
  borderRadius: 12,
  border: '1.5px solid #d1d5db',
  fontSize: 16,
  outline: 'none',
  boxSizing: 'border-box',
};

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e5e7eb', overflow: 'hidden', marginBottom: 16 }}>
      <div style={{ padding: '16px 20px', borderBottom: '1px solid #f3f4f6' }}>
        <p style={{ margin: 0, fontWeight: 700, fontSize: 14, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{title}</p>
      </div>
      <div style={{ padding: '20px' }}>
        {children}
      </div>
    </div>
  );
}

function Row({ label, value, action }: { label: string; value?: string; action: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 16, marginBottom: 16, borderBottom: '1px solid #f3f4f6' }}>
      <div>
        <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#111827' }}>{label}</p>
        {value && <p style={{ margin: '2px 0 0', fontSize: 13, color: '#6b7280' }}>{value}</p>}
      </div>
      {action}
    </div>
  );
}

type ActiveModal = 'name' | 'email' | 'password' | 'delete' | null;
type CalStatus = 'idle' | 'loading' | 'ready' | 'error';
type CalCopied = 'url' | 'google' | 'outlook' | null;

export function SettingsPage() {
  const navigate     = useNavigate();
  const user         = useAuthStore((s) => s.user);
  const accessToken  = useAuthStore((s) => s.accessToken);
  const setAuth      = useAuthStore((s) => s.setAuth);
  const logout       = useAuthStore((s) => s.logout);

  const contractor   = useContractorAuthStore((s) => s.contractor);

  const [modal, setModal]           = useState<ActiveModal>(null);
  const [nameValue, setNameValue]   = useState(user?.name ?? '');
  const [emailValue, setEmailValue] = useState(user?.email ?? '');
  const [password, setPassword]     = useState('');
  const [newPw, setNewPw]           = useState('');
  const [confirmPw, setConfirmPw]   = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [error, setError]           = useState('');
  const [success, setSuccess]       = useState('');
  const [loading, setLoading]       = useState(false);

  const [calToken, setCalToken]   = useState<string | null>(null);
  const [calStatus, setCalStatus] = useState<CalStatus>('idle');
  const [calCopied, setCalCopied] = useState<CalCopied>(null);
  const [revoking, setRevoking]   = useState(false);

  useEffect(() => {
    if (user) {
      setNameValue(user.name ?? '');
      setEmailValue(user.email ?? '');
    }
  }, [user]);

  function closeModal() {
    setModal(null);
    setPassword('');
    setNewPw('');
    setConfirmPw('');
    setDeleteConfirm('');
    setDeletePassword('');
    setError('');
    setSuccess('');
  }

  async function saveName() {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/auth/profile`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken ?? ''}` },
        body: JSON.stringify({ name: nameValue }),
      });
      const data = await res.json() as { data?: { name?: string }; error?: { message: string } };
      if (!res.ok) { setError(data.error?.message ?? 'Update failed.'); return; }
      user && setAuth({ ...user, name: data.data?.name ?? nameValue }, accessToken!, 900);
      setSuccess('Name updated.');
      setTimeout(closeModal, 1000);
    } catch { setError('Network error.'); }
    finally { setLoading(false); }
  }

  async function saveEmail() {
    if (!password) { setError('Enter your password to confirm.'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/auth/profile`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken ?? ''}` },
        body: JSON.stringify({ email: emailValue, password }),
      });
      const data = await res.json() as { data?: { email?: string }; error?: { message: string } };
      if (!res.ok) { setError(data.error?.message ?? 'Update failed.'); return; }
      user && setAuth({ ...user, email: data.data?.email ?? emailValue }, accessToken!, 900);
      setSuccess('Email updated.');
      setTimeout(closeModal, 1000);
    } catch { setError('Network error.'); }
    finally { setLoading(false); }
  }

  async function changePassword() {
    if (newPw !== confirmPw) { setError('New passwords do not match.'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/auth/change-password`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken ?? ''}` },
        body: JSON.stringify({ current_password: password, new_password: newPw }),
      });
      const data = await res.json() as { error?: { message: string } };
      if (!res.ok) { setError(data.error?.message ?? 'Change failed.'); return; }
      setSuccess('Password changed. Please log in again.');
      setTimeout(() => { logout(); navigate('/login', { replace: true }); }, 1500);
    } catch { setError('Network error.'); }
    finally { setLoading(false); }
  }

  async function deleteAccount() {
    if (deleteConfirm !== 'DELETE') { setError('Type DELETE to confirm.'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/auth/account`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken ?? ''}` },
        body: JSON.stringify({ confirmation: 'DELETE', password: deletePassword }),
      });
      const data = await res.json() as { error?: { message: string } };
      if (!res.ok) { setError(data.error?.message ?? 'Deletion failed.'); return; }
      logout();
      navigate('/', { replace: true });
    } catch { setError('Network error.'); }
    finally { setLoading(false); }
  }

  async function loadCalToken() {
    setCalStatus('loading');
    try {
      const res = await fetch(`${API_BASE}/calendar/token`, {
        credentials: 'include',
        headers: { Authorization: `Bearer ${accessToken ?? ''}` },
      });
      const data = await res.json() as { data?: { token: string }; error?: { message: string } };
      if (!res.ok || !data.data?.token) { setCalStatus('error'); return; }
      setCalToken(data.data.token);
      setCalStatus('ready');
    } catch {
      setCalStatus('error');
    }
  }

  async function revokeCalToken() {
    setRevoking(true);
    try {
      const res = await fetch(`${API_BASE}/calendar/token/revoke`, {
        method: 'POST',
        credentials: 'include',
        headers: { Authorization: `Bearer ${accessToken ?? ''}` },
      });
      const data = await res.json() as { data?: { token: string }; error?: { message: string } };
      if (res.ok && data.data?.token) {
        setCalToken(data.data.token);
        setCalCopied(null);
      }
    } catch { /* silent */ }
    finally { setRevoking(false); }
  }

  async function copyFeedUrl(target: 'url' | 'google' | 'outlook') {
    if (!calToken) return;
    await navigator.clipboard.writeText(
      `${window.location.origin}/api/v1/calendar/feed/${calToken}.ics`,
    );
    setCalCopied(target);
    setTimeout(() => setCalCopied(null), 3000);
  }

  const feedUrl = calToken
    ? `${window.location.origin}/api/v1/calendar/feed/${calToken}.ics`
    : null;
  const webcalUrl = feedUrl !== null ? feedUrl.replace(/^https?:\/\//, 'webcal://') : null;

  async function openBillingPortal() {
    setLoading(true);
    try {
      const res = await contractorApi.post<{ url: string }>('/contractor/billing/portal');
      window.location.href = res.url;
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to open billing portal.';
      alert(msg);
    } finally { setLoading(false); }
  }

  return (
    <>
      {/* Spinning keyframe */}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      <div style={{ maxWidth: 560, margin: '0 auto', padding: '24px 16px 80px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28 }}>
          <button
            type="button"
            onClick={() => navigate(-1)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
            aria-label="Back"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: 0 }}>Settings</h1>
        </div>

        {/* PROFILE */}
        <Section title="Profile">
          <Row
            label="Name"
            value={user?.name ?? '—'}
            action={
              <button
                type="button"
                onClick={() => { setNameValue(user?.name ?? ''); setModal('name'); }}
                style={{ fontSize: 14, color: '#1D4ED8', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer' }}
              >
                Edit
              </button>
            }
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#111827' }}>Email</p>
              <p style={{ margin: '2px 0 0', fontSize: 13, color: '#6b7280' }}>{user?.email ?? '—'}</p>
            </div>
            <button
              type="button"
              onClick={() => { setEmailValue(user?.email ?? ''); setModal('email'); }}
              style={{ fontSize: 14, color: '#1D4ED8', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer' }}
            >
              Edit
            </button>
          </div>
        </Section>

        {/* SECURITY */}
        <Section title="Security">
          <Row
            label="Password"
            value="Last changed recently"
            action={
              <button
                type="button"
                onClick={() => setModal('password')}
                style={{ fontSize: 14, color: '#1D4ED8', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer' }}
              >
                Change
              </button>
            }
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#111827' }}>Two-factor authentication</p>
              <p style={{ margin: '2px 0 0', fontSize: 13, color: '#6b7280' }}>TOTP via authenticator app</p>
            </div>
            <Link
              to="/app/settings/security"
              style={{ fontSize: 14, color: '#1D4ED8', fontWeight: 600, textDecoration: 'none' }}
            >
              Manage
            </Link>
          </div>
        </Section>

        {/* BILLING — contractors only */}
        {contractor && (
          <Section title="Billing">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#111827' }}>Subscription</p>
                <p style={{ margin: '2px 0 0', fontSize: 13, color: '#6b7280', textTransform: 'capitalize' }}>
                  {contractor.tier.replace('_', ' ')}
                </p>
              </div>
              <button
                type="button"
                onClick={() => void openBillingPortal()}
                disabled={loading}
                style={{
                  padding: '8px 16px', background: '#0f172a', color: '#fff',
                  borderRadius: 10, fontWeight: 600, fontSize: 14, border: 'none',
                  cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1,
                }}
              >
                Manage billing
              </button>
            </div>
            {contractor.tier === 'self_service' && (
              <button
                type="button"
                onClick={() => navigate('/contractor/signup?plan=pro')}
                style={{
                  width: '100%', padding: '12px', background: '#1D4ED8',
                  color: '#fff', borderRadius: 12, fontWeight: 600, fontSize: 14,
                  border: 'none', cursor: 'pointer',
                }}
              >
                Upgrade to Pro — $49/mo
              </button>
            )}
          </Section>
        )}

        {/* NOTIFICATIONS */}
        <Section title="Notifications">
          <p style={{ margin: 0, fontSize: 14, color: '#6b7280' }}>
            Manage notification preferences via the mobile app or contact{' '}
            <a href="mailto:support@housewizrd.com" style={{ color: '#1D4ED8' }}>support@housewizrd.com</a>.
          </p>
        </Section>

        {/* CALENDAR SYNC */}
        <Section title="Calendar Sync">
          {calStatus === 'idle' && (
            <div>
              <p style={{ margin: '0 0 16px', fontSize: 14, color: '#6b7280', lineHeight: 1.6 }}>
                Subscribe your maintenance schedule to Apple Calendar, Google Calendar, or Outlook. The feed updates automatically as tasks are added or rescheduled.
              </p>
              <button
                type="button"
                onClick={() => void loadCalToken()}
                style={{ padding: '10px 20px', background: '#1D4ED8', color: '#fff', borderRadius: 10, fontWeight: 600, fontSize: 14, border: 'none', cursor: 'pointer' }}
              >
                Get calendar link
              </button>
            </div>
          )}

          {calStatus === 'loading' && (
            <p style={{ margin: 0, fontSize: 14, color: '#6b7280' }}>Generating link…</p>
          )}

          {calStatus === 'error' && (
            <div>
              <p style={{ margin: '0 0 12px', fontSize: 14, color: '#dc2626' }}>Could not generate link. Try again.</p>
              <button
                type="button"
                onClick={() => void loadCalToken()}
                style={{ padding: '8px 16px', background: '#1D4ED8', color: '#fff', borderRadius: 8, fontWeight: 600, fontSize: 14, border: 'none', cursor: 'pointer' }}
              >
                Retry
              </button>
            </div>
          )}

          {calStatus === 'ready' && feedUrl && webcalUrl && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', marginBottom: 16 }}>
                  <code style={{ flex: 1, fontSize: 11, color: '#374151', wordBreak: 'break-all', lineHeight: 1.6 }}>
                    {feedUrl}
                  </code>
                  <button
                    type="button"
                    onClick={() => void copyFeedUrl('url')}
                    style={{ flexShrink: 0, padding: '6px 12px', background: calCopied === 'url' ? '#16a34a' : '#374151', color: '#fff', borderRadius: 6, fontWeight: 600, fontSize: 12, border: 'none', cursor: 'pointer' }}
                  >
                    {calCopied === 'url' ? 'Copied!' : 'Copy'}
                  </button>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                  <a
                    href={webcalUrl}
                    style={{ display: 'block', padding: '11px 16px', background: '#0f172a', color: '#fff', borderRadius: 10, fontWeight: 600, fontSize: 14, textDecoration: 'none', textAlign: 'center' }}
                  >
                    Add to Apple Calendar
                  </a>
                  <button
                    type="button"
                    onClick={() => void copyFeedUrl('google')}
                    style={{ padding: '11px 16px', background: calCopied === 'google' ? '#15803d' : '#fff', color: calCopied === 'google' ? '#fff' : '#374151', border: '1.5px solid #e5e7eb', borderRadius: 10, fontWeight: 600, fontSize: 14, cursor: 'pointer' }}
                  >
                    {calCopied === 'google'
                      ? 'Copied — paste in Google Calendar → Other calendars → Subscribe from URL'
                      : 'Copy for Google Calendar'}
                  </button>
                  <button
                    type="button"
                    onClick={() => void copyFeedUrl('outlook')}
                    style={{ padding: '11px 16px', background: calCopied === 'outlook' ? '#15803d' : '#fff', color: calCopied === 'outlook' ? '#fff' : '#374151', border: '1.5px solid #e5e7eb', borderRadius: 10, fontWeight: 600, fontSize: 14, cursor: 'pointer' }}
                  >
                    {calCopied === 'outlook'
                      ? 'Copied — paste in Outlook → Add calendar → Subscribe from web'
                      : 'Copy for Outlook'}
                  </button>
                </div>

                <p style={{ margin: '0 0 16px', fontSize: 12, color: '#9ca3af', lineHeight: 1.5 }}>
                  Calendar apps check for updates roughly once a day — new and rescheduled tasks appear at the next refresh, not instantly.
                </p>

                <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: 16 }}>
                  <button
                    type="button"
                    onClick={() => void revokeCalToken()}
                    disabled={revoking}
                    style={{ padding: '8px 16px', background: 'none', border: '1.5px solid #fecaca', color: '#dc2626', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: revoking ? 'not-allowed' : 'pointer', opacity: revoking ? 0.6 : 1 }}
                  >
                    {revoking ? 'Regenerating…' : 'Revoke and regenerate link'}
                  </button>
                  <p style={{ margin: '6px 0 0', fontSize: 12, color: '#9ca3af' }}>
                    Existing subscriptions using the old link will stop receiving updates.
                  </p>
                </div>
              </div>
          )}
        </Section>

        {/* LEGAL */}
        <Section title="Legal">
          <Row
            label="Privacy Policy"
            action={
              <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ fontSize: 14, color: '#1D4ED8', fontWeight: 600, textDecoration: 'none' }}>
                View →
              </a>
            }
          />
          <Row
            label="Terms of Service"
            action={
              <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ fontSize: 14, color: '#1D4ED8', fontWeight: 600, textDecoration: 'none' }}>
                View →
              </a>
            }
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#111827' }}>Early Access Disclaimer</p>
            <a href="/disclaimer" target="_blank" rel="noopener noreferrer" style={{ fontSize: 14, color: '#1D4ED8', fontWeight: 600, textDecoration: 'none' }}>
              View →
            </a>
          </div>
        </Section>

        {/* DANGER ZONE */}
        <Section title="Danger Zone">
          <p style={{ margin: '0 0 16px', fontSize: 14, color: '#6b7280' }}>
            Permanently delete your account and all associated data. This cannot be undone.
          </p>
          <button
            type="button"
            onClick={() => setModal('delete')}
            style={{
              padding: '10px 20px', border: '1.5px solid #fecaca',
              background: '#fff', color: '#dc2626', borderRadius: 10,
              fontWeight: 600, fontSize: 14, cursor: 'pointer',
            }}
          >
            Delete account
          </button>
        </Section>
      </div>

      {/* MODALS */}
      {modal && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
            zIndex: 1000, padding: '0 0 env(safe-area-inset-bottom)',
          }}
        >
          <div style={{
            background: '#fff', borderRadius: '20px 20px 0 0', width: '100%',
            maxWidth: 560, padding: '28px 24px 40px', display: 'flex', flexDirection: 'column', gap: 16,
          }}>
            {modal === 'name' && (
              <>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Change name</h2>
                <input
                  type="text"
                  value={nameValue}
                  onChange={(e) => setNameValue(e.target.value)}
                  autoFocus
                  style={inputStyle}
                />
                {error && <p style={{ margin: 0, color: '#dc2626', fontSize: 14 }}>{error}</p>}
                {success && <p style={{ margin: 0, color: '#16a34a', fontSize: 14 }}>{success}</p>}
                <button type="button" onClick={() => void saveName()} disabled={loading}
                  style={{ padding: '14px', background: '#1D4ED8', color: '#fff', borderRadius: 12, fontWeight: 600, fontSize: 16, border: 'none', cursor: 'pointer' }}>
                  {loading ? 'Saving…' : 'Save'}
                </button>
              </>
            )}

            {modal === 'email' && (
              <>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Change email</h2>
                <input type="email" value={emailValue} onChange={(e) => setEmailValue(e.target.value)} placeholder="New email" style={inputStyle} />
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Current password" autoComplete="current-password" style={inputStyle} />
                {error && <p style={{ margin: 0, color: '#dc2626', fontSize: 14 }}>{error}</p>}
                {success && <p style={{ margin: 0, color: '#16a34a', fontSize: 14 }}>{success}</p>}
                <button type="button" onClick={() => void saveEmail()} disabled={loading}
                  style={{ padding: '14px', background: '#1D4ED8', color: '#fff', borderRadius: 12, fontWeight: 600, fontSize: 16, border: 'none', cursor: 'pointer' }}>
                  {loading ? 'Saving…' : 'Save'}
                </button>
              </>
            )}

            {modal === 'password' && (
              <>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Change password</h2>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Current password" autoComplete="current-password" style={inputStyle} />
                <input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="New password (12+ chars)" autoComplete="new-password" style={inputStyle} />
                <input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} placeholder="Confirm new password" autoComplete="new-password" style={inputStyle} />
                {error && <p style={{ margin: 0, color: '#dc2626', fontSize: 14 }}>{error}</p>}
                {success && <p style={{ margin: 0, color: '#16a34a', fontSize: 14 }}>{success}</p>}
                <button type="button" onClick={() => void changePassword()} disabled={loading}
                  style={{ padding: '14px', background: '#1D4ED8', color: '#fff', borderRadius: 12, fontWeight: 600, fontSize: 16, border: 'none', cursor: 'pointer' }}>
                  {loading ? 'Changing…' : 'Change password'}
                </button>
              </>
            )}

            {modal === 'delete' && (
              <>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#dc2626' }}>Delete account</h2>
                <p style={{ margin: 0, fontSize: 14, color: '#6b7280' }}>
                  This permanently deletes your account and all data. Type <strong>DELETE</strong> to confirm.
                </p>
                <input type="text" value={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.value)} placeholder="Type DELETE" style={inputStyle} />
                <input type="password" value={deletePassword} onChange={(e) => setDeletePassword(e.target.value)} placeholder="Your password" autoComplete="current-password" style={inputStyle} />
                {error && <p style={{ margin: 0, color: '#dc2626', fontSize: 14 }}>{error}</p>}
                <button type="button" onClick={() => void deleteAccount()} disabled={loading || deleteConfirm !== 'DELETE'}
                  style={{ padding: '14px', background: deleteConfirm === 'DELETE' ? '#dc2626' : '#e5e7eb', color: deleteConfirm === 'DELETE' ? '#fff' : '#9ca3af', borderRadius: 12, fontWeight: 600, fontSize: 16, border: 'none', cursor: loading ? 'not-allowed' : 'pointer' }}>
                  {loading ? 'Deleting…' : 'Permanently delete account'}
                </button>
              </>
            )}

            <button type="button" onClick={closeModal}
              style={{ padding: '12px', background: 'none', border: '1.5px solid #e5e7eb', borderRadius: 12, fontWeight: 600, fontSize: 15, color: '#374151', cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  );
}
